import React from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import {
    Avatar,
    AvatarType,
    FontVariant,
    Text,
    InteractiveElement,
    ThemeConsumer,
    Icon,
    IconTypes,
    Tooltip,
} from '@pexip/components';

import {TestId} from '../../../test/testIds';

import styles from './SuggestionItem.module.scss';
import {KEYBOARD_EVENT_KEY} from '../../constants';

export const SuggestionItemView: React.FC<{
    id?: string;
    title?: string;
    meetingId: string;
    imageUrl?: string;
    isMobile: boolean;
    className?: string;
    isFavorite: boolean;
    isBrandingFavorite: boolean;
    onClick: () => void;
    onFavoriteToggle: () => void;
}> = ({
    id,
    title,
    meetingId,
    imageUrl,
    isMobile,
    className,
    isFavorite,
    isBrandingFavorite,
    onClick,
    onFavoriteToggle,
}) => {
    const {t} = useTranslation();
    const defaultId = React.useId();
    const isFavoriteToggleDisabled = isBrandingFavorite;

    return (
        <ThemeConsumer>
            {({colorScheme: defaultColorScheme}) => (
                <InteractiveElement
                    id={id ?? defaultId}
                    htmlTag="div"
                    role="option"
                    tabIndex={0}
                    data-keyboard-nav-item
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === KEYBOARD_EVENT_KEY.enter) {
                            onClick();
                        }
                    }}
                    onClick={onClick}
                    className={cx(defaultColorScheme, styles.item, className)}
                    focusClassName={styles.focus}
                    data-testid={TestId.SuggestionItem}
                >
                    <div className={styles.avatarAndInfo}>
                        <Avatar
                            className={styles.avatar}
                            sizeModifier="small"
                            imageUrl={imageUrl}
                            type={
                                imageUrl
                                    ? AvatarType.Image
                                    : AvatarType.Initials
                            }
                            displayName={title ?? meetingId}
                        />
                        <div className={styles.info}>
                            <Text
                                title={title ?? meetingId}
                                fontVariant={FontVariant.Body}
                            >
                                {title ?? meetingId}
                            </Text>
                            {title && meetingId && (
                                <Text
                                    title={meetingId}
                                    fontVariant={
                                        isMobile
                                            ? FontVariant.Body
                                            : FontVariant.Small
                                    }
                                >
                                    {meetingId}
                                </Text>
                            )}
                        </div>
                    </div>
                    <Tooltip
                        tooltipContainerClassName={
                            styles.iconFavoriteToggleTooltip
                        }
                        text={t(
                            'media.call-history.remove-favorite-disabled-branding',
                            'Added by your admin and can not be removed.',
                        )}
                        isTooltipEnabled={isFavoriteToggleDisabled}
                    >
                        <InteractiveElement
                            aria-disabled={isFavoriteToggleDisabled}
                            className={cx(styles.iconFavoriteToggleContainer, {
                                [styles.iconFavoriteToggleContainerDisabled]:
                                    isFavoriteToggleDisabled,
                            })}
                            data-testid={
                                TestId.ButtonSuggestionItemFavoriteToggle
                            }
                            htmlTag="div"
                            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                if (isFavoriteToggleDisabled) {
                                    e.stopPropagation();
                                    return;
                                }
                                e.stopPropagation();
                                onFavoriteToggle();
                            }}
                            tabIndex={0}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLDivElement>,
                            ) => {
                                if (e.key === KEYBOARD_EVENT_KEY.enter) {
                                    if (isFavoriteToggleDisabled) {
                                        e.stopPropagation();
                                        return;
                                    }
                                    e.stopPropagation();
                                    onFavoriteToggle();
                                }
                            }}
                        >
                            <Icon
                                className={cx(styles.iconFavoriteToggle, {
                                    [styles.isFavorite]: isFavorite,
                                    [styles.iconFavoriteToggleDisabled]:
                                        isFavoriteToggleDisabled,
                                })}
                                source={IconTypes.IconStar}
                            />
                        </InteractiveElement>
                    </Tooltip>
                </InteractiveElement>
            )}
        </ThemeConsumer>
    );
};

export type SuggestionItemProps = React.ComponentProps<
    typeof SuggestionItemView
>;
